import {
  Box,
  Checkbox,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import {
  CustomeDialog,
  Loader,
  LoadingButton,
  InputControl,
} from "../../../shared";
import { useImmerReducer } from "use-immer";
import InfiniteScroll from "react-infinite-scroll-component";
import { getIngredientList } from "../../Ingredients/api/IngredientApi";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { Close } from "@mui/icons-material";
import qs from "qs";

export const AddIngredientDialog = ({
  open,
  onAdd,
  onCancel,
  existingIngredients = [],
}) => {
  const { state, handleToggle, loadNextPage, updateSearch } =
    useAddIngredientDialog({
      existingIngredients,
      open,
    });

  const [searchInput, setSearchInput] = useState("");

  const onSearch = (e) => {
    updateSearch(e.target.value);
    setSearchInput(e.target.value);
  };
  return (
    <CustomeDialog
      title={
        <Stack
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          p="16px"
          borderColor="divider"
          height="52px"
          width="100%"
          gap="8px"
        >
          <Typography fontWeight={500} variant="body1" fontSize="20px">
            Ingredients
          </Typography>
          <InputControl
            type="table-search"
            size="small"
            placeholder="Search by Ingredient, Suppliers or Category tags"
            value={searchInput}
            onChange={onSearch}
            sx={{
              "&:focus-within": {
                width: { sm: "400px" },
              },
              transition: "0.5s",
            }}
            endAdornment={
              searchInput && (
                <InputAdornment
                  position="end"
                  onClick={() => onSearch({ target: { value: "" } })}
                  sx={{ cursor: "pointer" }}
                >
                  <Close size="small" />
                </InputAdornment>
              )
            }
          />
        </Stack>
      }
      open={open}
      divider={false}
      content={
        <Box
          id="scrollableDiv"
          sx={{
            height: "52vh",
            overflow: "auto",
          }}
        >
          {state.isLoading && state.loadedIngredients.length === 0 ? (
            <Loader />
          ) : (
            <InfiniteScroll
              scrollableTarget="scrollableDiv"
              dataLength={state.loadedIngredients.length}
              next={loadNextPage}
              hasMore={state.lastPagination?.hasMorePages}
              loader={<Box textAlign="center">Loading...</Box>}
            >
              {state.loadedIngredients.length === 0 ? (
                <Stack
                  justifyContent="center"
                  alignItems="center"
                  height="52vh"
                >
                  <Typography variant="caption">No records found</Typography>
                </Stack>
              ) : (
                <List dense>
                  {state.loadedIngredients.map((ingredient) => {
                    const isDisabled = existingIngredients.includes(
                      ingredient.id,
                    );
                    const isChecked =
                      isDisabled ||
                      state.selectedItems.some((i) => i.id === ingredient.id);

                    return (
                      <ListItemButton
                        dense
                        key={ingredient.id}
                        disablePadding
                        onClick={() => handleToggle(ingredient)}
                      >
                        <ListItemIcon>
                          <Checkbox
                            edge="end"
                            checked={isChecked}
                            disabled={isDisabled}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemText primary={ingredient.name} />
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
            </InfiniteScroll>
          )}
        </Box>
      }
      footer={{
        display: true,
        confirmButton: (
          <LoadingButton
            variant="contained"
            size="small"
            onClick={() => {
              onCancel();
              onAdd(state.selectedItems);
            }}
            loading={false}
          >
            Save
          </LoadingButton>
        ),
        onConfirm: true,
        onCancel: onCancel,
      }}
      sx={{
        pt: "16px",
        px: "2px",
        pb: "8px",
        width: { sm: "600px", xs: "100%" },
      }}
    />
  );
};

const reducer = (draft, action) => {
  switch (action.type) {
    case "TOGGLE_ITEM": {
      const ingredient = action.payload;
      const idx = draft.selectedItems.findIndex((i) => i.id === ingredient.id);

      if (idx > -1) {
        draft.selectedItems.splice(idx, 1);
      } else {
        draft.selectedItems.push(ingredient);
      }
      break;
    }

    case "CLEAR_SELECTION": {
      draft.selectedItems = [];
      break;
    }

    case "SET_LOADING": {
      draft.isLoading = action.payload;
      break;
    }

    case "SET_SEARCH": {
      draft.search = action.payload;
      break;
    }

    case "SET_PAGE_DATA": {
      const payload = action.payload || {};
      const ingredients =
        payload.ingredients ?? payload.data?.ingredients ?? [];

      const pagination = payload.pagination ??
        payload.data?.pagination ?? { hasMorePages: false };

      if (draft.pagination.pageIndex === 1) {
        draft.loadedIngredients = ingredients;
      } else {
        ingredients.forEach((ing) => {
          if (!draft.loadedIngredients.some((i) => i.id === ing.id)) {
            draft.loadedIngredients.push(ing);
          }
        });
      }

      draft.lastPagination = pagination;
      break;
    }

    case "INCREMENT_PAGE": {
      draft.pagination.pageIndex += 1;
      break;
    }

    case "RESET_PAGINATION": {
      draft.pagination.pageIndex = 1;
      draft.loadedIngredients = [];
      draft.lastPagination = { hasMorePages: false };
      break;
    }

    default:
      break;
  }
};

const useAddIngredientDialog = ({ open, existingIngredients }) => {
  const [state, dispatch] = useImmerReducer(reducer, {
    selectedItems: [],
    search: "",
    pagination: { pageIndex: 1, pageSize: 50 },
    loadedIngredients: [],
    lastPagination: { hasMorePages: false },
    isLoading: false,
  });

  const updateSearch = useDebouncedCallback((input) => {
    dispatch({ type: "SET_SEARCH", payload: input });

    // FULL RESET when search changes
    dispatch({ type: "RESET_PAGINATION" });

    // Fetch first page with new search
    fetchPage(1, input);
  }, 500);

  const queryClient = useQueryClient();

  const fetchPage = async (pageIndex, search) => {
    dispatch({ type: "SET_LOADING", payload: true });

    try {
      const sort = { "sort[name]": "asc" };
      const query = {
        params: search ? { ...sort, search } : sort,
        paramsSerializer: (params) => qs.stringify(params, { encode: true }),
      };

      const data = await queryClient.fetchQuery({
        queryKey: ["IngredientList", search, pageIndex],
        queryFn: () =>
          getIngredientList({
            query,
            pagination: { pageIndex, pageSize: 50 },
          }),
        staleTime: 0,
      });

      dispatch({ type: "SET_PAGE_DATA", payload: data });
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  };

  const loadNextPage = () => {
    if (state.isLoading || !state.lastPagination?.hasMorePages) return;
    dispatch({ type: "INCREMENT_PAGE" });
  };

  const handleToggle = (ingredient) => {
    if (!existingIngredients.includes(ingredient.id)) {
      dispatch({ type: "TOGGLE_ITEM", payload: ingredient });
    }
  };

  // Initial load
  useEffect(() => {
    if (!open) return;
    dispatch({ type: "RESET_PAGINATION" });
    dispatch({ type: "CLEAR_SELECTION" });
    fetchPage(1, "");
  }, [open]);

  // Pagination
  useEffect(() => {
    if (!open || state.pagination.pageIndex === 1) return;
    fetchPage(state.pagination.pageIndex, state.search);
  }, [state.pagination.pageIndex]);

  return {
    state,
    handleToggle,
    loadNextPage,
    updateSearch,
  };
};
